import { NotFoundPage } from "@/components/NotFoundPage";
import { TooltipProvider } from "@/components/ui/tooltip";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { routeTree } from "./routeTree.gen";
import type { RouterContext } from "./routes/__root";

export function getEnvironment() {
  if (document.location.hostname.includes("preview")) {
    return "preview" as const;
  }

  if (document.location.hostname.includes("fiberplane.com")) {
    return "production" as const;
  }

  return "local" as const;
}

const environment = getEnvironment();

Sentry.init({
  dsn: "https://d2abd1023bd0226267f9e6c5a9b99ceb@o1302973.ingest.us.sentry.io/4509679942107136",
  integrations: [
    // Automatically instrument browsers
    Sentry.browserTracingIntegration(),
    // Send console logs to Sentry
    Sentry.consoleLoggingIntegration(),
  ],
  _experiments: { enableLogs: true },
  environment,
  enabled: environment !== "local",
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/fiberplane.com\/api\//],
});

export const rootElement = document.getElementById("root");
if (rootElement === null) {
  throw new Error("Root element not found");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Always consider data stale
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Don't retry auth errors (401, 403)
        if (
          error instanceof Error &&
          "statusCode" in error &&
          (error.statusCode === 401 || error.statusCode === 403)
        ) {
          return false;
        }
        // Otherwise, retry up to 3 times
        return failureCount < 3;
      },
    },
  },
});

// Create a router with initial auth state
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: {},
  } satisfies RouterContext,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0, // Always consider preloaded data stale
  defaultStaleTime: 0, // Always consider data stale
  defaultNotFoundComponent: NotFoundPage,
});

// Provide type safety for the router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(rootElement).render(
  <StrictMode>
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </TooltipProvider>
  </StrictMode>,
);
